import React from 'react';
import styled from 'styled-components';
import { Tooltip } from 'react-tooltip';
import SearchInput from './SearchInput';
import FileUpload from '../common/FileUpload';
import PrimaryActionButton from './PrimaryActionButton';
import PhotoIcon from '../../svgs/PhotoIcon';
import SortIcon from '@src/svgs/SortIcon';
import { PhotoArtifactTab } from '../../types.ts/story';
import { SortBy } from '../../utility/general';

interface PhotoTabActionsProps {
  tab: PhotoArtifactTab;
  sortBy: SortBy;
  uploadButtonActive: boolean;
  onSearch: (searchTerm: string) => void;
  onSort: () => void;
  onUploadClick: () => void;
  onUploadCallback: () => Promise<void>;
  setIsLoading: (loading: boolean) => void;
}

const PhotoTabActions: React.FC<PhotoTabActionsProps> = ({
  tab,
  sortBy,
  uploadButtonActive,
  onSearch,
  onSort,
  onUploadClick,
  onUploadCallback,
  setIsLoading,
}) => {
  const renderStockTabActions = () => (
    <PhotoTopAction>
      <SearchInput
        placeholder="Search photo"
        handleAction={onSearch}
        radius="5px"
        iconRight
      />
    </PhotoTopAction>
  );

  const renderAiTabActions = () => (
    <PhotoTopAction>
      <SearchInput
        placeholder="Describe the photo you want to see and hit enter"
        handleAction={onSearch}
        expandHeight
        hideIcons
        radius="5px"
      />
    </PhotoTopAction>
  );

  const renderArtifactTabActions = () => (
    <PhotoTopAction>
      <SearchSortWrapper>
        <SearchInput
          placeholder="Search"
          handleAction={onSearch}
          radius="5px"
          iconRight
        />
        <SortIconWrapper
          onClick={onSort}
          data-tooltip-id="sort-tooltip"
          data-tooltip-content={`Sort by Upload Date: ${
            sortBy === '-createdAt' ? 'Oldest' : 'Newest'
          }`}
        >
          <SortIcon />
          <Tooltip
            id="sort-tooltip"
            style={TOOLTIP_STYLE}
            place="top"
            positionStrategy="fixed"
          />
        </SortIconWrapper>
      </SearchSortWrapper>
      <FileUpload
        width="100%"
        type={(() => {
          if (tab === PhotoArtifactTab.story) return 'storyArtifacts';
          if (tab === PhotoArtifactTab.logo) return 'organizationLogos';
          return 'organizationArtifacts';
        })()}
        Button={
          <AddFileButton isActivated={uploadButtonActive}>
            <PhotoIcon strokeColor="#03041A" /> Upload Photo
          </AddFileButton>
        }
        onClick={onUploadClick}
        callback={onUploadCallback}
        setIsLoading={setIsLoading}
      />
    </PhotoTopAction>
  );

  switch (tab) {
    case PhotoArtifactTab.stock:
      return renderStockTabActions();
    case PhotoArtifactTab.ai:
      return renderAiTabActions();
    case PhotoArtifactTab.story:
    case PhotoArtifactTab.album:
    case PhotoArtifactTab.logo:
      return renderArtifactTabActions();
    default:
      return null;
  }
};

export default PhotoTabActions;

const PhotoTopAction = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SearchSortWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
  > form {
    flex: 1;
  }
`;

const SortIconWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 8px;
`;

const AddFileButton = styled(PrimaryActionButton)``;

const TOOLTIP_STYLE = {
  padding: '8px',
  fontSize: '12px',
  lineHeight: '120%',
  zIndex: '10',
  fontWeight: 600,
  color: '#F3E9D7',
  background: '#484848',
};